import L10n from 'helpers/L10n';
import { L10Common } from 'helpers/l10n/L10nCommon';
import { L10nFilters } from 'helpers/l10n/L10nFilters';
import { L10ManageMembers } from 'helpers/l10n/L10nManageMembers';

export const MEETING_TYPE_FILTER_OPTIONS = [
  { label: L10n.filters.meetingTypeInternal, value: 'internal' },
  { label: L10n.filters.meetingTypeExternal, value: 'external' }
];

export const MEETING_PRIVACY_FILTER_OPTIONS = [
  { label: L10Common.private, value: 'private' },
  { label: L10Common.organization, value: 'organization' },
  { label: L10ManageMembers.primaryTeamAccess, value: 'team' },
  { label: L10Common.public, value: 'public' }
];

export const DEAL_RISK_FILTER_OPTIONS = [
  {
    value: 'low',
    label: (
      <div className='flex items-center justify-between'>
        <span>Low</span>
        <span className='w-14 rounded bg-green-lightest py-0.5 text-center text-xs font-bold text-green-dark'>
          0-33
        </span>
      </div>
    )
  },
  {
    value: 'medium',
    label: (
      <div className='flex items-center justify-between'>
        <span>Medium</span>
        <span className='w-14 rounded bg-yellow-lightest py-0.5 text-center text-xs font-bold text-yellow-more-dark'>
          34-66
        </span>
      </div>
    )
  },
  {
    value: 'high',
    label: (
      <div className='flex items-center justify-between'>
        <span>High</span>
        <span className='w-14 rounded bg-red-lightest py-0.5 text-center text-xs font-bold text-red-dark'>
          67-100
        </span>
      </div>
    )
  }
];

export const QUALIFICATION_SCORE_FILTER_OPTIONS = [
  {
    value: 'weak',
    label: (
      <div className='flex items-center justify-between'>
        <span>Weak</span>
        <span className='w-14 rounded bg-red-lightest py-0.5 text-center text-xs font-bold text-red-dark'>
          0-50
        </span>
      </div>
    )
  },
  {
    value: 'moderate',
    label: (
      <div className='flex items-center justify-between'>
        <span>Moderate</span>
        <span className='w-14 rounded bg-yellow-light-scale py-0.5 text-center text-xs font-bold text-yellow-darker'>
          51-75
        </span>
      </div>
    )
  },
  {
    value: 'strong',
    label: (
      <div className='flex items-center justify-between'>
        <span>Strong</span>
        <span className='w-14 rounded bg-green-lightest py-0.5 text-center text-xs font-bold text-green-dark'>
          76-100
        </span>
      </div>
    )
  }
];

export const DEAL_STATUS_FILTER_OPTIONS = [
  { value: 'open_only', label: 'Open' },
  { value: 'closed_won_only', label: 'Closed Won' },
  { value: 'closed_lost_only', label: 'Closed Lost' }
];

export const DATE_RANGE_FILTER = {
  dateOptions: [
    'today',
    'yesterday',
    'lastSeven',
    'lastThirty',
    'lastNinety',
    'lastWeek',
    'lastMonth',
    'lastQuarter',
    'weekToDate',
    'monthToDate',
    'quarterToDate',
    'allTime',
    'custom'
  ]
};

export const DATE_FILTER_VALUES = {
  keys: {
    dateRange: 'dateRange'
  },
  values: {
    lastThirty: 'lastThirty'
  }
};

export const MORE_FILTER_MEETING_OPTIONS = {
  showUnrecordedFilter: true,
  defaultValues: {
    includeInternalMeetings: true,
    showInternalOnly: undefined,
    sharedMeetingsOnly: undefined,
    showUnrecordedMeetings: undefined
  }
};

export const UPCOMING_MORE_FILTER_MEETING_OPTIONS = {
  defaultValues: {
    includeInternalMeetings: true,
    showInternalOnly: false,
    isConference: false,
    showRecordingEnabled: false,
    showUnrecordedMeetings: undefined
  }
};

export const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const TIMELINE_OPTIONS = [
  { value: 'recent', label: 'Past' },
  { value: 'upcoming', label: 'Upcoming' }
];

export const SHOW_MEETINGS_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'external', label: 'External' },
  { value: 'internal', label: 'Internal' }
];

export const SHOW_MEETING_FILTER_VALUES = {
  all: 'all',
  internal: 'internal',
  external: 'external'
};

export const AI_WIN_LOSS_ANALYSIS_DATE_FILTER_OPTIONS = [
  'today',
  'yesterday',
  'lastSeven',
  'lastThirty',
  'lastNinety',
  'lastWeek',
  'lastMonth',
  'lastQuarter',
  'thisMonth',
  'thisQuarter',
  'thisYear',
  'weekToDate',
  'monthToDate',
  'quarterToDate',
  'allTime',
  'custom'
];

export const FILTERS_PARAMS_OMITTED_FROM_UPCOMING_MEETINGS = [
  'meeting_outcome_uuid', // Outcomes
  'custom_category', // Smart Categories
  'recording_duration__gte', // Min Duration
  'recording_duration__lte', // Max Duration
  'start_at__gte', // Start Date
  'end_at__lte', // End Date
  'recording_duration__isnull', // Recorded Meetings only
  'shared_meetings_only' // Shared Meetings only
];

export const FILTERS_PARAMS_OMITTED_FROM_CALENDAR_VIEW_MEETINGS = [
  'recording_enabled', // If the meeting is set to be recorded
  'is_conference' // Conf details check
];

export const FILTERS_PARAMS_OMITTED_FROM_RECENT_MEETINGS = [
  'recording_enabled', // If the meeting is set to be recorded
  'is_conference' // Conf details check
];

export const QUICK_FILTERS_GROUP_KEY = 'Quick Filters';
const MEETINGS_GROUP_KEY = 'Meetings';
const CALLS_GROUP_KEY = 'Calls';
const ACTIONS_GROUP_KEY = 'Actions';
const COMPANY_GROUP_KEY = 'Company';

const COMMON_DASHBOARD_FILTERS = {
  teams: {
    label: 'Team',
    value: 'teams',
    groupKey: QUICK_FILTERS_GROUP_KEY
  },
  users: {
    label: 'Member',
    value: 'users',
    groupKey: QUICK_FILTERS_GROUP_KEY
  },
  accounts: {
    label: 'Company Name',
    value: 'accounts',
    groupKey: COMPANY_GROUP_KEY,
    isCrmFilterGroup: true
  },
  types: {
    label: 'Purpose',
    value: 'types',
    groupKey: QUICK_FILTERS_GROUP_KEY
  },
  outcomes: {
    label: 'Outcome',
    value: 'outcomes',
    groupKey: MEETINGS_GROUP_KEY
  },
  keywordsInTranscript: {
    label: 'Keywords',
    value: 'keywordsInTranscript',
    groupKey: MEETINGS_GROUP_KEY
  },
  saidBy: {
    label: 'Said By',
    value: 'saidBy',
    groupKey: MEETINGS_GROUP_KEY
  },
  customCategory: {
    label: 'Smart Topic',
    value: 'customCategory',
    groupKey: MEETINGS_GROUP_KEY
  },
  stageNow: {
    label: 'Stage Now',
    value: 'stageNow',
    groupKey: MEETINGS_GROUP_KEY,
    properties: {
      identifier: 'uuid'
    }
  },
  stageDuring: {
    label: 'Stage During',
    value: 'stageDuring',
    groupKey: MEETINGS_GROUP_KEY,
    properties: {
      identifier: 'uuid'
    }
  },
  amountNow: {
    label: 'Amount Now',
    value: 'amountNow',
    groupKey: MEETINGS_GROUP_KEY
  }
};

const MEETING_FILTERS_UPCOMING_TIMELINE = {
  showRecordingEnabled: {
    label: 'Recording Enabled',
    value: 'showRecordingEnabled',
    fieldType: 'meetingBoolean',
    groupKey: MEETINGS_GROUP_KEY
  },
  isConference: {
    label: 'Conferencing details only',
    value: 'isConference',
    fieldType: 'meetingBoolean',
    groupKey: MEETINGS_GROUP_KEY
  },
  meetingType: {
    label: 'Meeting Type',
    value: 'meetingType',
    groupKey: MEETINGS_GROUP_KEY,
    properties: {
      options: MEETING_TYPE_FILTER_OPTIONS
    }
  }
};

const MEETING_FILTERS_RECENT_TIMELINE = {
  sharedMeetingsOnly: {
    label: 'Meeting Shared',
    value: 'sharedMeetingsOnly',
    fieldType: 'meetingBoolean',
    groupKey: ACTIONS_GROUP_KEY
  },
  showUnrecordedMeetings: {
    label: 'Meeting Status',
    value: 'showUnrecordedMeetings',
    fieldType: 'meetingBoolean',
    groupKey: MEETINGS_GROUP_KEY
  },
  meetingDuration: {
    label: 'Meeting Duration',
    value: 'meetingDuration',
    groupKey: MEETINGS_GROUP_KEY,
    properties: {
      isRange: true
    }
  },
  meetingType: {
    label: 'Meeting Type',
    value: 'meetingType',
    groupKey: MEETINGS_GROUP_KEY,
    properties: {
      options: MEETING_TYPE_FILTER_OPTIONS
    }
  },
  meetingsScored: {
    label: 'Meeting Scored',
    value: 'meetingsScored',
    fieldType: 'meetingBoolean',
    groupKey: ACTIONS_GROUP_KEY
  },
  addedToPlaylist: {
    label: 'Meeting Added to playlist',
    value: 'addedToPlaylist',
    fieldType: 'meetingBoolean',
    groupKey: ACTIONS_GROUP_KEY
  },
  meetingsHasSnippets: {
    label: 'Meeting has snippets',
    value: 'meetingsHasSnippets',
    fieldType: 'meetingBoolean',
    groupKey: ACTIONS_GROUP_KEY
  },
  meetingsHasComments: {
    label: 'Meeting has comments',
    value: 'meetingsHasComments',
    fieldType: 'meetingBoolean',
    groupKey: ACTIONS_GROUP_KEY
  },
  meetingPrivacy: {
    label: 'Meeting Privacy',
    value: 'meetingPrivacy',
    groupKey: MEETINGS_GROUP_KEY,
    properties: {
      optionChoices: MEETING_PRIVACY_FILTER_OPTIONS
    }
  },
  videoConferencing: {
    label: 'Video Conferencing',
    value: 'videoConferencing',
    groupKey: MEETINGS_GROUP_KEY,
    properties: { optionChoices: [] }
  }
};

export const MEETING_DASHBOARD_FILTERS = {
  upcoming: {
    ...COMMON_DASHBOARD_FILTERS,
    ...MEETING_FILTERS_UPCOMING_TIMELINE
  },
  recent: {
    date: {
      label: 'Date Range',
      value: 'date',
      groupKey: QUICK_FILTERS_GROUP_KEY
    },
    ...COMMON_DASHBOARD_FILTERS,
    ...MEETING_FILTERS_RECENT_TIMELINE
  }
};

export const CALLS_DASHBOARD_FILTERS = {
  date: {
    label: 'Date Range',
    value: 'date',
    groupKey: QUICK_FILTERS_GROUP_KEY
  },
  ...Object.entries(COMMON_DASHBOARD_FILTERS).reduce<Record<string, any>>(
    (acc, [key, filter]) => {
      acc[key] =
        filter.groupKey === MEETINGS_GROUP_KEY
          ? { ...filter, groupKey: CALLS_GROUP_KEY }
          : filter;
      return acc;
    },
    {}
  ),
  sharedMeetingsOnly: {
    label: 'Call Shared',
    value: 'sharedMeetingsOnly',
    fieldType: 'meetingBoolean',
    groupKey: ACTIONS_GROUP_KEY
  },
  call: {
    label: 'Call Type',
    value: 'call',
    groupKey: CALLS_GROUP_KEY,
    properties: {
      optionChoices: {
        callsOnly: L10n.filters.callsOnly,
        answered: L10n.filters.isAnsweredFilter,
        outbound: L10n.filters.outboundFilter,
        inbound: L10n.filters.inboundFilter
      }
    }
  },
  meetingDuration: {
    label: 'Call Duration',
    value: 'meetingDuration',
    groupKey: CALLS_GROUP_KEY,
    properties: {
      isRange: true
    }
  },
  meetingsScored: {
    label: 'Call Scored',
    value: 'meetingsScored',
    fieldType: 'meetingBoolean',
    groupKey: ACTIONS_GROUP_KEY
  },
  addedToPlaylist: {
    label: 'Call Added to playlist',
    value: 'addedToPlaylist',
    fieldType: 'meetingBoolean',
    groupKey: ACTIONS_GROUP_KEY
  },
  meetingsHasSnippets: {
    label: 'Call has snippets',
    value: 'meetingsHasSnippets',
    fieldType: 'meetingBoolean',
    groupKey: ACTIONS_GROUP_KEY
  },
  meetingsHasComments: {
    label: 'Call has comments',
    value: 'meetingsHasComments',
    fieldType: 'meetingBoolean',
    groupKey: ACTIONS_GROUP_KEY
  },
  meeting_selection: {
    label: 'Meetings',
    value: 'meeting_selection',
    groupKey: CALLS_GROUP_KEY
  }
};

export const NEXT_MEETING_EXISTS_FILTER_OPTIONS = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' }
];

export const COMPANY_DASHBOARD_DEFAULT_FILTERS_KEYS = {
  company_annual_revenue: 'annualRevenue',
  crm_obj_owner: 'owner'
};

export const CONTACT_DASHBOARD_DEFAULT_FILTERS_KEYS = {
  crm_obj_owner: 'owner'
};

export const LEAD_DASHBOARD_DEFAULT_FILTERS_KEYS = {
  crm_obj_owner: 'owner'
};

export const DEALS_DASHBOARD_DEFAULT_FILTERS_KEYS = {
  crm_obj_owner: 'owner',
  deal_risk: 'dealRisk',
  deal_qualification_score: 'qualificationScore',
  deal_forecast_category: 'forecastCategory',
  deal_stage_now: 'dealStage',
  crm_account_selection: 'customAccounts'
};

export const MEETING_DASHBOARD_DEFAULT_FILTERS_KEYS = {
  member_selection: 'customMembers',
  meeting_organizer: 'customMembers',
  team_selection: 'customTeams',
  meeting_purpose: 'customTypes',
  meeting_outcome: 'customOutcomes',
  company_selection: 'customMeetingAccounts',
  deal_stage_during: 'customStageDuring',
  deal_stage_now: 'customStageNow',
  custom_category: 'customMeetingCategories',
  keyword_search: 'keywordsInTranscript'
};

export const CALLS_DASHBOARD_DEFAULT_FILTERS_KEYS = {
  member_selection: 'customMembers',
  meeting_organizer: 'customMembers',
  team_selection: 'customTeams',
  meeting_purpose: 'customTypes',
  meeting_outcome: 'customOutcomes',
  company_selection: 'customMeetingAccounts',
  deal_stage_during: 'customStageDuring',
  deal_stage_now: 'customStageNow',
  custom_category: 'customMeetingCategories',
  keyword_search: 'keywordsInTranscript',
  meeting_selection: 'meetingSelection'
};

export const TASKS_DASHBOARD_DEFAULT_FILTERS_KEYS = {
  task_assignee: 'customMembers',
  team_selection: 'customTeams',
  task_created_by: 'customMembers',
  task_associated_meeting: 'meetingSelection',
  meeting_organizer: 'customMembers',
  task_last_updated_by: 'customMembers',
  company_selection: 'customMeetingAccounts',
  crm_obj_owner: 'owner'
};

export const CUSTOM_FILTERS_DASHBOARD_VIEW_LIST = [
  'company',
  'contact',
  'lead',
  'deal',
  'meeting',
  'calls',
  'trackers',
  'tasks',
  'forecast'
];

export const CUSTOM_FILTERS_MEETINGS_VIEW_LIST = [
  'allMeetings',
  'globalSearch',
  'calls'
];

export const CUSTOM_FILTERS_VIEW_LIST = [
  'company',
  'contact',
  'lead',
  'deals',
  'forecast',
  'trackers',
  'tasks',
  ...CUSTOM_FILTERS_MEETINGS_VIEW_LIST
];

export const DEFAULT_CUSTOM_FILTERS = {
  filterGroups: [],
  orderBy: [],
  groupBy: [],
  searchPhrase: ''
};

export const FILTERS_ALLOWED_ONCE = [
  'keyword_search',
  'said_by',
  'meeting_start_at'
];

export const FILTERS_FETCH_TYPES = {
  MEETINGS: 'filter_meetings',
  CALLS: 'filter_calls',
  GLOBALSEARCH: 'filter_search'
};

export const FILTERS_TOOLTIP_MAP = {
  member_selection: L10nFilters.memberFilterHelperText,
  keyword_search: L10nFilters.keywordFilterHelperText
};

export const INVALID_FILTER_REASON_MAP = {
  VALUE_DELETED: 'value_deleted',
  FILTER_DELETED: 'filter_deleted'
};

export const CUSTOM_FILTERS = {
  groupKeysToIgnoreForFilterChangeComparison: [
    'group_name',
    'order',
    'group_uuid'
  ],
  ruleKeysToIgnoreForFilterChangeComparison: [
    'order',
    'details',
    'invalid_filter_details'
  ],
  invalidFilterDetailsReasonList: Object.values(INVALID_FILTER_REASON_MAP)
};

export const RECORDING_DURATION_MAX_LIMIT = '1000';

export const RECORDING_DURATION_DISPLAY_SUFFIX = 'mins';

export const SAVED_VIEW_TYPE_MAPPING = {
  COMPANY: 'company',
  DEAL: 'deal',
  MEETING: 'meeting',
  CALLS: 'calls',
  TRACKERS: 'trackers',
  CONTACT: 'contact',
  LEAD: 'lead',
  TASKS: 'tasks',
  FORECAST: 'forecast'
};

export const ALLOWED_VIEWS_FOR_WEEKLY_DIGEST_DYNAMIC_FILTERATION = [
  'usage/roi',
  'usage',
  'usage/activity'
];

export const DATE_FILTER_NOT_DEFINED_OPTION_VALUE = 'notDefined';

// Dynamic filter tokens - must match backend constants
export const DYNAMIC_FILTERS = {
  ME: {
    token: '_dynamic_me_',
    label: 'Me (Current User)'
  },
  TEAMS_I_MANAGE: {
    token: '_dynamic_teams_i_manage_',
    label: 'Teams managed by me (Current user)'
  }
};
export const DYNAMIC_FILTER_TOOLTIP = (
  <div>
    <p className='text-xs font-bold'>Smart filter</p>
    <p className='text-xs'>The values adapt based on the logged in user</p>
  </div>
);

export const DEFAULT_MEETING_FILTER_PAYLOAD = {
  view_type: 'default',
  time_range: 'past',
  filter_groups: [
    {
      group_operator: 'and',
      rules: [
        {
          rule_uid: 'meeting_status',
          operator: 'equal',
          values: ['recorded'],
          field_type: 'select',
          rule_object_type: null,
          rule_operator_in_group: 'and',
          details: {
            viewTypes: [
              'default',
              'calendar',
              'global_search',
              'trackers',
              'alerts'
            ],
            timeRanges: ['past', 'upcoming', 'all']
          },
          invalid_filter_details: null
        }
      ]
    }
  ],
  order_by: []
};
